import { useCallback, useState, useMemo } from 'react';
import { useCrmOrgsApiFacade } from '../../../../apis/facades/useCrmOrgsApiFacade';
import { useSweepFieldsApiFacade } from '../../../../apis/facades/useSweepFieldsApiFacade';
import colors from '@sweep-io/sweep-design/dist/colors';
import {
  SlackActionButtonTextField,
  customButtonSx,
  showOnlyUpdatable,
  SlackActionRow,
} from './common';
import { Box, FormControl, MenuItem, SelectChangeEvent } from '@mui/material';
import FieldLabel from '../../../common/FieldLabel';
import { SweepObjectSelector } from '../../../common/sweep-object-field-selectors/SweepObjectSelector';
import { SelectorValueTypes } from '../../../../types/enums/SelectorValueTypes';
import { Plus } from '@sweep-io/sweep-design/dist/icons';
import { SlackActionType } from '../../../../types/enums/SlackActionType';
import {
  NestedFieldsSelector,
  NestedFieldsField,
} from '../../../common/fieldsSelectors/NestedFieldsSelector';
import SweepSelect from '../../../common/SweepSelect';
import { SingleRecordTypeSelector } from '../SingleRecordTypeSelector';
import { SlackFields } from '../SlackFields';
import { useRunOnce } from '../../../common/useRunOnce';
import { uniqueId } from '../../../../lib/uniqueId';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';

export const SlackActionCreate = ({
  crmOrgId,
  objectName,
  onUpdateAction,
  action,
  idx,
  buttonAction,
}: {
  crmOrgId: string;
  objectName: string;
  onUpdateAction: (action: CreateRecordSlackAction, index: number) => any;
  action: CreateRecordSlackAction;
  idx: number;
  buttonAction: React.ReactNode;
}) => {
  const { get_sweepFields } = useSweepFieldsApiFacade();
  const { getObjectFunnelsAndRecordTypes } = useCrmOrgsApiFacade();
  const [recordTypesList, setRecordTypesList] = useState<ShortParserRecordType[]>([]);
  const [relationShipFields, setRelationShipFields] = useState<SweepFieldObjectReferenceDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const showRecordTypeSelector = recordTypesList.length > 1;
  const showRelationShipFieldsSelector = relationShipFields.length > 1;

  const loadRecordTypes = useCallback(
    async (objectName: string) => {
      try {
        const { recordTypes } = await getObjectFunnelsAndRecordTypes({
          crmOrgId,
          objectName,
        });
        const filteredTypes = recordTypes.filter((rt) => rt.name !== '__MASTER__');
        return filteredTypes;
      } catch (error) {
        return [];
      }
    },
    [crmOrgId, getObjectFunnelsAndRecordTypes],
  );

  const loadRelationShipFields = useCallback(
    async (_objectName: string) => {
      try {
        const fields = await get_sweepFields({
          objectType: [_objectName],
          crmOrgId,
          includeDescriptionValueSetNameRequired: true,
        });

        const isCreateable = (filedId: string) => {
          const elem = fields?.sweepFields[0]?.fields?.find((el) => el.id === filedId);
          return elem?.properties?.createable;
        };

        const relationShipFields =
          fields.sweepFields[0].references?.filter((f) => f.objectNames.includes(objectName)) || [];

        const filteredFields = relationShipFields.filter((el) => isCreateable(el.fieldId));

        return {
          fields,
          relationShipFields: filteredFields,
        };
      } catch (error) {
        return {
          fields: { sweepFields: [{ fields: [], references: [] }] },
          relationShipFields: [],
        };
      }
    },
    [crmOrgId, get_sweepFields, objectName],
  );

  const processFieldsAndRelationships = useCallback(
    (
      fields: any,
      relationShipFields: SweepFieldObjectReferenceDto[],
      recordTypes: ShortParserRecordType[],
    ) => {
      const requiredFields = fields.sweepFields[0].fields.filter((field: any) => field?.isRequired);

      let relationshipField: [string] | undefined = undefined;
      if (relationShipFields.length > 0) {
        relationshipField =
          relationShipFields.length === 1 ? [relationShipFields[0].fieldId] : [''];
      }

      let recordType: ShortParserRecordType | undefined = undefined;
      if (recordTypes.length > 0) {
        recordType = recordTypes.length === 1 ? recordTypes[0] : { label: '', name: '' };
      }

      const newFields: AutomationActionField[] = requiredFields.map((f: any) => ({
        _fieldIds: [f.id || ''],
        _isMandatory: f.isRequired,
        _id: uniqueId(),
        value: '',
        valueType: SelectorValueTypes.LITERAL,
      }));

      return {
        fields: newFields,
        relationshipField,
        recordType,
      };
    },
    [],
  );

  useRunOnce(() => {
    if (action.objectTypeName.objectType) {
      loadRecordTypes(action.objectTypeName.objectType).then(setRecordTypesList);
    }
    if (action.relationshipField) {
      loadRelationShipFields(action.objectTypeName.objectType).then(({ relationShipFields }) =>
        setRelationShipFields(relationShipFields),
      );
    }
  });

  const handleObjectChange = useCallback(
    async (value: string) => {
      setIsLoading(true);
      setRecordTypesList([]);

      try {
        const newAction: CreateRecordSlackAction = {
          buttonText: action.buttonText,
          fields: [],
          type: SlackActionType.CREATE_RECORD,
          objectTypeName: { objectType: value, label: value },
        };

        onUpdateAction(newAction, idx);

        const [recordTypes, { fields, relationShipFields }] = await Promise.all([
          loadRecordTypes(value),
          loadRelationShipFields(value),
        ]);

        setRecordTypesList(recordTypes);
        setRelationShipFields(relationShipFields);

        const processedData = processFieldsAndRelationships(
          fields,
          relationShipFields,
          recordTypes,
        );

        onUpdateAction(
          {
            ...newAction,
            ...processedData,
            objectTypeName: {
              objectType: value,
              label: value,
              recordType: processedData.recordType,
            },
          },
          idx,
        );
      } catch (error) {
        // Error handling is done in the individual functions
      } finally {
        setIsLoading(false);
      }
    },
    [
      action,
      idx,
      loadRecordTypes,
      loadRelationShipFields,
      onUpdateAction,
      processFieldsAndRelationships,
    ],
  );

  const addingFieldsIsDisabled = !action.objectTypeName.objectType;
  const plusIconColor = addingFieldsIsDisabled ? colors.grey[500] : colors.blue[500];

  const excludeFieldIds = useMemo(
    () => action.fields?.flatMap((f) => f._fieldIds || []).filter(Boolean),
    [action.fields],
  );

  return (
    <>
      <SlackActionRow>
        {buttonAction}
        <Box>
          <FieldLabel label="Object" markLabelAsRequired />
          <SweepObjectSelector
            crmOrgId={crmOrgId}
            value={action.objectTypeName.objectType}
            objectTypeFilters={{
              createable: true,
            }}
            onChange={handleObjectChange}
          />
          {isLoading && <CenteredCircularProgress size={20} />}
        </Box>
        {showRecordTypeSelector && (
          <Box>
            <FieldLabel label="Record Type" markLabelAsRequired />
            <SingleRecordTypeSelector
              recordTypesList={recordTypesList}
              crmOrgId={crmOrgId}
              objectName={action.objectTypeName.objectType}
              recordType={action.objectTypeName.recordType}
              onChange={(recordType) => {
                onUpdateAction(
                  {
                    ...action,
                    objectTypeName: {
                      ...action.objectTypeName,
                      recordType,
                    },
                  },
                  idx,
                );
              }}
            />
          </Box>
        )}
        {showRelationShipFieldsSelector && (
          <Box>
            <FieldLabel label="Relationship field" markLabelAsRequired />
            <SweepSelect
              SelectProps={{
                placeholder: 'Relationship field',
                value: action.relationshipField?.[0] ?? '',
                onChange: (event: SelectChangeEvent<unknown>) => {
                  const value = event.target.value as string;
                  onUpdateAction(
                    {
                      ...action,
                      relationshipField: [value],
                    },
                    idx,
                  );
                },
              }}
            >
              {relationShipFields.map((field) => (
                <MenuItem key={field.fieldId} value={field.fieldId}>
                  {field.refName}
                </MenuItem>
              ))}
            </SweepSelect>
          </Box>
        )}
        <SlackActionButtonTextField action={action} idx={idx} onUpdateAction={onUpdateAction} />
      </SlackActionRow>
      <SlackActionRow>
        <FormControl
          sx={{
            width: '100%',
            marginBottom: '20px',
          }}
        >
          <FieldLabel label={'Fields to create'} markLabelAsRequired={true} />
          <SlackFields
            actionFields={action.fields.map((f) => ({
              isRequired: f._isMandatory,
              fieldIds: f._fieldIds,
              fieldData: f,
            }))}
            onChange={(_, fields) => {
              onUpdateAction({ ...action, fields }, idx);
            }}
          />
          <Box>
            <NestedFieldsSelector
              filterBy={showOnlyUpdatable}
              disableLookupItemsResolve={true}
              crmOrgId={crmOrgId}
              objectType={action.objectTypeName.objectType}
              customButtonStartIcon={<Plus size="medium" color={plusIconColor} />}
              useCustomButton
              customButtonText="Add field"
              nestedPath={[]}
              customButtonSx={customButtonSx}
              excludeFieldIds={excludeFieldIds}
              onChange={(sweepField: NestedFieldsField) => {
                if (action.type === SlackActionType.CREATE_RECORD) {
                  onUpdateAction(
                    {
                      ...action,
                      fields: [
                        ...action.fields,
                        {
                          _fieldIds: sweepField.fieldIds,
                          _isMandatory: false,
                          _id: uniqueId(),
                          value: '',
                          valueType: SelectorValueTypes.LITERAL,
                          fieldType: '',
                        },
                      ],
                    },
                    idx,
                  );
                }
              }}
              readonly={addingFieldsIsDisabled}
            />
          </Box>
        </FormControl>
      </SlackActionRow>
    </>
  );
};
